<template>
	<div
		class="resource-planning-user-select"
		data-testId="resource-planning-user-select"
	>
		<SuiSelectList ref="selectList">
			<template #header>
				<SuiSelectListSwitch
					v-if="enableRolesSelect && useRolesSetting"
					v-model="selectedView"
					:items="switchItems"
				/>
				<SuiSelectListHeaderWithSearch
					ref="suiSelectListHeaderWithSearch"
					:search-query="searchQuery"
					:search-placeholder="
						$t('resource-booking-select-search-placeholder')
					"
					@search="onSearchUsed"
					@close="$emit('close')"
				>
					<template #title>
						{{ displayTitle }}
					</template>
					<template #close>
						{{ $t('close') }}
					</template>
				</SuiSelectListHeaderWithSearch>
			</template>

			<Transition
				name="resource-planning-user-select-transition"
				mode="out-in"
			>
				<div v-if="isLoading">
					<ResourcePlanningUserSelectSkeleton />
				</div>
				<div v-else-if="selectedView === SWITCH_ITEM.ROLES">
					<template v-if="roleListFiltered.length > 0">
						<SuiSelectListButton
							v-for="role in roleListFiltered"
							:key="role.id"
							class="resource-planning-user-select__role-button"
							@click="onRoleClick(role.id)"
						>
							{{ role.name }}
						</SuiSelectListButton>
					</template>
					<SuiSelectListEmpty v-else>
						{{ $t('search.noResults') }}
					</SuiSelectListEmpty>
				</div>
				<div v-else-if="useRolesSetting">
					<template v-if="usersWithoutRoles.length > 0">
						<ResourcePlanningUserSelectButton
							v-for="user in usersWithoutRoles"
							:key="user.id"
							:user="user"
							@click="onUserClick(user.id, user.name)"
						/>
					</template>

					<template v-if="rolesSubGroupedWithUsers.length > 0">
						<template
							v-for="role in rolesSubGroupedWithUsers"
							:key="role.roleId"
						>
							<SuiSelectListHelpers
								class="resource-planning-user-select__group-wrapper"
								@click="toggleRoleGroupDropdown(role.roleId)"
							>
								<h3
									class="resource-planning-user-select__role-name"
								>
									{{ role.roleName }}
								</h3>
								<SuiIcon
									name="icon-arrow-down-16"
									class="resource-planning-user-select__expand-icon"
									:class="{
										'resource-planning-user-select__expand-icon--rotate':
											!role.isOpen,
									}"
								/>
							</SuiSelectListHelpers>
							<div v-if="role.isOpen" :aria-label="role.roleName">
								<SuiSelectListButton
									v-if="shouldShowRolePlaceholder(role)"
									class="resource-planning-user-select__role-placeholder-button"
									@click="
										onRolePlaceholderClick(role.roleData)
									"
								>
									<SuiAvatar
										:aria-label="role.roleName"
										size="x-small"
										:initials="
											role.roleData?.initials ??
											getInitials(role.roleName)
										"
										:color="
											role.roleData?.hslColor ?? undefined
										"
										show-border
										variant="dashed"
									/>
									<p>{{ role.roleName }}</p>
								</SuiSelectListButton>

								<ResourcePlanningUserSelectButton
									v-for="user in role.userList"
									:key="user.id"
									:user="user"
									@click="onUserClick(user.id, user.name)"
								/>
							</div>
						</template>
					</template>

					<SuiSelectListEmpty v-else>
						{{
							searchQuery.length === 0
								? $t(
										'resource-booking-select-all-users-members'
									)
								: $t('search.noResults')
						}}
					</SuiSelectListEmpty>
				</div>
				<div
					v-else-if="userListFiltered.length > 0"
					data-testId="user-list"
					class="resource-planning-user-select__user-list"
				>
					<template
						v-for="group in userGroupListToDisplay"
						:key="group.groupId"
					>
						<SuiSelectListHelpers
							v-if="userGroupListToDisplay.length > 1"
							class="resource-planning-user-select__group-wrapper"
							@click="toggleUserGroupDropdown(group.groupId)"
						>
							<h3
								class="resource-planning-user-select__role-name"
							>
								{{ group.groupName }}
							</h3>
							<SuiIcon
								name="icon-arrow-down-16"
								class="resource-planning-user-select__expand-icon"
								:class="{
									'resource-planning-user-select__expand-icon--rotate':
										!group.isOpen,
								}"
							/>
						</SuiSelectListHelpers>
						<div v-if="group.isOpen" :aria-label="group.groupName">
							<ResourcePlanningUserSelectButton
								v-for="user in group.userList"
								:key="user.id"
								:user="user"
								@click="onUserClick(user.id, user.name)"
							/>
						</div>
					</template>
				</div>
				<SuiSelectListEmpty v-else>
					{{
						searchQuery.length === 0
							? $t('resource-booking-select-all-users-members')
							: $t('search.noResults')
					}}
				</SuiSelectListEmpty>
			</Transition>
		</SuiSelectList>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	computed,
	reactive,
	watch,
	type ComponentPublicInstance,
	nextTick,
	onMounted,
	onUnmounted,
} from 'vue';
import {
	SuiSelectListHeaderWithSearch,
	SuiSelectList,
	SuiIcon,
	SuiSelectListButton,
	SuiSelectListHelpers,
	SuiSelectListEmpty,
	SuiSelectListSwitch,
	SuiAvatar,
} from '@developers/scoro-ui';
import {
	INITIAL_USER_GROUP_DROPDOWN_STATE,
	ROLE_LIST_QUERY_VARIABLES,
	SWITCH_ITEM,
	USER_GROUP,
	USER_LIST_USERS_INPUT,
} from './constants';
import {
	type Project,
	useResourcePlanningUserSelectQuery,
	useResourcePlanningUserSelectRolesLazyQuery,
	UserStatus,
	type Role,
	type User,
	useResourcePlanningUserSelectProjectRolesQuery,
	type RoleProject,
	TYPES,
} from '@/generated/graphql';
import { useBooleanSetting } from '@/composables/settings/useSettings';
import { Setting } from '@/constants/settings';
import { useTranslation } from '@/composables';
import ResourcePlanningUserSelectButton from './ResourcePlanningUserSelectButton.vue';
import ResourcePlanningUserSelectSkeleton from './ResourcePlanningUserSelectSkeleton.vue';
import { type ValueOf } from '@/types';
import { useResourcePlanningStore } from '../../store';
import { storeToRefs } from 'pinia';
import { getInitials } from '@/features/User/utils';
import { getRoleToRoleProjectMap } from '../ResourcePlanningGantt/ResourcePlanningGanttUtils';

export interface ResourcePlanningUserSelectProps {
	title?: string;
	enableRolesSelect?: boolean;
	excludeUserId?: User['id'] | null;
	projectId?: Project['id'];
	selectedEntityId?: User['id'] | Role['id'] | null;
	selectedEntityType?: typeof TYPES.User | typeof TYPES.RoleProject;
	withRolePlaceholders?: boolean;
}

const {
	excludeUserId = null,
	projectId = '',
	selectedEntityId = null,
	selectedEntityType = TYPES.User,
	withRolePlaceholders = false,
	title = '',
} = defineProps<ResourcePlanningUserSelectProps>();

const emit = defineEmits<{
	(event: 'user-selected', id: User['id'], name: User['name']): void;
	(
		event: 'role-selected',
		id: Role['id'],
		roleProjectId: RoleProject['id'] | undefined
	): void;
	(event: 'close'): void;
}>();

const [useRolesSetting] = useBooleanSetting(Setting.USE_ROLES);
// Need to access the global pinia as SPA and MFE are using different instances
const store =
	window.__MFE_UTILS__?.pinia.resourcePlanningStore ??
	useResourcePlanningStore();
const { selectedUserIds } = storeToRefs(store);
const { t } = useTranslation();

const excludedUserIds = computed(() => {
	return excludeUserId !== undefined
		? new Set([excludeUserId])
		: selectedUserIds.value;
});

const selectList = ref<InstanceType<typeof SuiSelectList>>();
const searchQuery = ref('');
const suiSelectListHeaderWithSearch = ref<ComponentPublicInstance>();

const selectedView = ref<ValueOf<typeof SWITCH_ITEM>>(SWITCH_ITEM.INDIVIDUALS);
const switchItems = [
	{
		value: SWITCH_ITEM.INDIVIDUALS,
		label: t('individuals'),
	},
	{
		value: SWITCH_ITEM.ROLES,
		label: t('roles'),
	},
];

const displayTitle = computed(() => {
	if (title) {
		return title;
	}

	return selectedView.value === SWITCH_ITEM.INDIVIDUALS
		? t('resource-booking-select-title')
		: t('resource-booking-select-role');
});

const { result, loading } = useResourcePlanningUserSelectQuery({
	usersListInput: USER_LIST_USERS_INPUT,
});

const {
	result: rolesResult,
	onResult: onRolesResult,
	load: loadRoles,
	loading: loadingRoles,
} = useResourcePlanningUserSelectRolesLazyQuery(
	{
		...ROLE_LIST_QUERY_VARIABLES,
	},
	{
		notifyOnNetworkStatusChange: true,
	}
);

const { result: resultProjectRoles, loading: loadingProjectRoles } =
	useResourcePlanningUserSelectProjectRolesQuery(
		{
			projectInput: {
				projectId: Number(projectId),
			},
		},
		{
			enabled: computed(() => withRolePlaceholders && Boolean(projectId)),
		}
	);

const isLoading = computed(
	() => loading.value || loadingRoles.value || loadingProjectRoles.value
);

onRolesResult((res) => {
	if (!res.data) {
		return;
	}
	if (res.data.RolesList) {
		res.data.RolesList.forEach((role) => {
			roleGroupDropdownState.push({ id: role.id, isOpen: true });
		});

		roleGroupDropdownState.push({
			id: USER_GROUP.PROJECT_MEMBERS,
			isOpen: true,
		});
	}

	resetMinHeight();
});

watch(
	useRolesSetting,
	(newValue) => {
		if (newValue) {
			loadRoles();
		}
	},
	{ immediate: true }
);

const userList = computed(
	() =>
		result.value?.UsersList.filter(
			(user) =>
				!excludedUserIds.value.has(user.id) &&
				user.status !== UserStatus.Inactive
		) ?? []
);

const matchesSearchValue = (name: string) => {
	return name
		.toLowerCase()
		.trim()
		.includes(searchQuery.value.toLowerCase().trim());
};

const roleToRoleProjectMap = computed(() =>
	getRoleToRoleProjectMap(resultProjectRoles.value?.Project?.roleProjects)
);

const userListFiltered = computed(() => {
	if (searchQuery.value && useRolesSetting.value) {
		return (
			userList.value.filter(
				(user) =>
					matchesSearchValue(user.role?.name ?? '') ||
					matchesSearchValue(user.name)
			) ?? []
		);
	} else if (searchQuery.value) {
		return (
			userList.value.filter((user) => matchesSearchValue(user.name)) ?? []
		);
	}

	return userList.value;
});

const roleListFiltered = computed(() => {
	return (
		rolesResult.value?.RolesList.filter((role) =>
			matchesSearchValue(role.name)
		) || []
	);
});

const usersWithoutRoles = computed(() => {
	return userListFiltered.value.filter((user) => !user.role?.id);
});

const projectMembersUserList = computed(() => {
	if (!userListFiltered.value) {
		return [];
	}

	return userListFiltered.value.filter(
		(user) =>
			user.id !== excludeUserId && selectedUserIds.value.has(user.id)
	);
});

const unAssignedUserList = computed(() => {
	if (!userListFiltered.value) {
		return [];
	}

	return userListFiltered.value.filter(
		(user) =>
			user.id !== excludeUserId && !selectedUserIds.value.has(user.id)
	);
});

const userGroupDropdownState = reactive(INITIAL_USER_GROUP_DROPDOWN_STATE);

const userGroupListToDisplay = computed(() => {
	const groups = [
		{
			groupName: t('project_header_project_members'),
			groupId: USER_GROUP.PROJECT_MEMBERS,
			userList: projectMembersUserList.value,
			isOpen: userGroupDropdownState.find(
				(roleState) => roleState.id === USER_GROUP.PROJECT_MEMBERS
			)?.isOpen,
		},
		{
			groupName: t('project_header_other_users'),
			groupId: USER_GROUP.OTHER_USERS,
			userList: unAssignedUserList.value,
			isOpen: userGroupDropdownState.find(
				(groupState) => groupState.id === USER_GROUP.OTHER_USERS
			)?.isOpen,
		},
	];

	return groups.filter((group) => group.userList.length > 0);
});

const selectedRoleId = computed(
	() =>
		resultProjectRoles.value?.Project?.roleProjects?.find(
			(roleProject) => roleProject.id === selectedEntityId
		)?.role.id
);

const roleGroupDropdownState = reactive<Array<{ id: string; isOpen: boolean }>>(
	[]
);

const resetMinHeight = async () => {
	await nextTick();

	const selectListContent = ref(
		selectList.value?.$refs.selectListContent as HTMLDivElement
	);

	setTimeout(() => {
		selectListContent.value.style.minHeight = `${selectListContent.value.offsetHeight}px`;
	}, 310);
};

const findRoleUsers = (roleId: string[]) =>
	userListFiltered.value.filter((user) => roleId.includes(user.id));

const rolesSubGroupedWithUsers = computed(() => {
	if (rolesResult?.value?.RolesList) {
		const projectMembersGroup = [
			{
				roleName: t('project_header_project_members'),
				roleId: USER_GROUP.PROJECT_MEMBERS,
				userList: projectMembersUserList.value,
				roleData: null,
				isOpen: roleGroupDropdownState.find(
					(roleState) => roleState.id === USER_GROUP.PROJECT_MEMBERS
				)?.isOpen,
			},
		];

		const roleGroups = rolesResult.value.RolesList.map((item) => {
			const userIds = item.users.reduce<Array<string>>((acc, item) => {
				if (!selectedUserIds.value.has(item.id)) {
					acc.push(item.id);
				}

				return acc;
			}, []);

			const roleProjectId = roleToRoleProjectMap.value.get(item.id);

			return {
				roleName: item.name,
				roleId: item.id,
				userList: findRoleUsers(userIds),
				roleData: withRolePlaceholders
					? {
							id: item.id,
							roleProjectId: roleProjectId ?? undefined,
							name: item.name,
							hslColor: item.hslColor,
							initials: item.initials,
						}
					: null,
				isOpen: roleGroupDropdownState.find(
					(roleState) => roleState.id === item.id
				)?.isOpen,
			};
		});

		if (selectedRoleId.value) {
			const selectedRoleGroup: typeof roleGroups = [];
			const otherRoleGroups: typeof roleGroups = [];

			roleGroups.forEach((item) => {
				const isSelected = item.roleId === selectedRoleId.value;

				if (isSelected) {
					selectedRoleGroup.push(item);
				} else {
					otherRoleGroups.push(item);
				}
			});

			return [
				...projectMembersGroup,
				...selectedRoleGroup,
				...otherRoleGroups,
			].filter(
				({ userList, roleData }) =>
					userList.length > 0 || (withRolePlaceholders && roleData)
			);
		}

		return [...projectMembersGroup, ...roleGroups].filter(
			({ userList, roleData }) =>
				userList.length > 0 || (withRolePlaceholders && roleData)
		);
	}

	return [];
});

const onSearchUsed = (value: string) => {
	searchQuery.value = value;
};

const toggleUserGroupDropdown = (groupId: string) => {
	if (groupId) {
		const index = userGroupDropdownState.findIndex(
			(groupState) => groupState.id === groupId
		);

		userGroupDropdownState[index].isOpen =
			!userGroupDropdownState[index].isOpen;
	}
};

const toggleRoleGroupDropdown = (roleId: string) => {
	if (roleId) {
		const index = roleGroupDropdownState.findIndex(
			(roleState) => roleState.id === roleId
		);

		roleGroupDropdownState[index].isOpen =
			!roleGroupDropdownState[index].isOpen;
	}
};

const shouldShowRolePlaceholder = (role: {
	roleData?: { id: RoleProject['id'] } | null;
}) => {
	return (
		withRolePlaceholders &&
		role.roleData &&
		!(
			selectedEntityType === TYPES.RoleProject &&
			role.roleData.id === selectedEntityId
		)
	);
};

const onUserClick = (id: User['id'], name: User['name']) => {
	emit('user-selected', id, name);
	emit('close');
};

const onRoleClick = (id: Role['id']) => {
	const roleProjectId = roleToRoleProjectMap.value.get(id);
	emit('role-selected', id, roleProjectId);
	emit('close');
};

const onRolePlaceholderClick = (roleData: { id: Role['id'] } | null) => {
	if (roleData?.id) {
		onRoleClick(roleData.id);
	}
};

onMounted(async () => {
	await nextTick();
	suiSelectListHeaderWithSearch.value?.$el.querySelector('input')?.focus();

	const background = document.createElement('div');

	background.className = 'resource-planning-user-select__background';
	background.setAttribute(
		'data-testid',
		'resource-planning-user-select-background'
	);
	background.addEventListener('click', () => {
		emit('close');
	});

	document.body.appendChild(background);
});

onUnmounted(() => {
	const background = document.querySelector(
		'.resource-planning-user-select__background'
	);

	background?.remove();
});
</script>

<style lang="scss" scoped>
.resource-planning-user-select {
	&__group-wrapper {
		padding: 1.5rem 1.5rem 1.5rem 2rem;

		&:hover {
			cursor: pointer;
		}
	}

	&__role-name {
		font-size: 1.625rem;
		font-weight: $scoro-fw-semi-bold;
	}

	&__expand-icon {
		margin-left: auto;
		transition: transform 0.2s ease;

		&--rotate {
			transform: rotate(-90deg);
		}
	}

	&__role-button {
		padding-left: 2rem;
	}

	&__role-placeholder-button {
		padding-left: 1rem;

		&:hover {
			background-color: $neutral-10;
		}
	}

	&__user-list {
		@media only screen and (max-height: $breakpoint-small) {
			max-height: 30vh;
		}
	}
}

.resource-planning-user-select-transition {
	&-enter-active {
		transition: opacity 0.3s ease;
	}

	&-leave-active {
		transition: none;
	}

	&-enter-from,
	&-leave-to {
		opacity: 0;
	}
}
</style>

<style lang="scss">
.resource-planning-user-select__background {
	z-index: z('modal-popup');
	inset: 0;
	position: fixed;
}
</style>
